type KeyboardVisibilityManagerDeps = {
  getShadowRoot: () => ShadowRoot | null;
  onKeyboardVisibilityChange: (isOpen: boolean) => void;
};

export class KeyboardVisibilityManager {
  private viewportBaseHeight = 0;
  private readonly getShadowRoot: KeyboardVisibilityManagerDeps['getShadowRoot'];
  private readonly onKeyboardVisibilityChange: KeyboardVisibilityManagerDeps['onKeyboardVisibilityChange'];

  constructor({ getShadowRoot, onKeyboardVisibilityChange }: KeyboardVisibilityManagerDeps) {
    this.getShadowRoot = getShadowRoot;
    this.onKeyboardVisibilityChange = onKeyboardVisibilityChange;
  }

  start(): void {
    globalThis.visualViewport?.addEventListener('resize', this.handleViewportResize);
  }

  stop(): void {
    globalThis.visualViewport?.removeEventListener('resize', this.handleViewportResize);
  }

  handleFocusIn = (): void => {
    this.updateKeyboardState();
  };

  handleFocusOut = (): void => {
    // Wait one frame so focus/viewport settle before deciding keyboard state.
    requestAnimationFrame(() => this.updateKeyboardState());
  };

  private readonly handleViewportResize = (): void => {
    this.updateKeyboardState();
  };

  private isInputFocused(): boolean {
    const shadowRoot = this.getShadowRoot();
    const input = shadowRoot?.querySelector<HTMLTextAreaElement>('.message-input');
    return !!input && input === shadowRoot?.activeElement;
  }

  private updateKeyboardState(): void {
    const visualViewport = globalThis.visualViewport;
    const inputFocused = this.isInputFocused();

    if (!visualViewport) {
      this.onKeyboardVisibilityChange(inputFocused);
      return;
    }

    if (this.viewportBaseHeight === 0) {
      this.viewportBaseHeight = visualViewport.height;
    }

    if (!inputFocused && visualViewport.height > this.viewportBaseHeight) {
      this.viewportBaseHeight = visualViewport.height;
    }

    const viewportDelta = this.viewportBaseHeight - visualViewport.height;
    const isLikelyKeyboardOpen = inputFocused && viewportDelta > 120;
    this.onKeyboardVisibilityChange(isLikelyKeyboardOpen);
  }
}
